/**
 * When a habit was paused, as epoch ms; `end` is null while the pause is
 * ongoing. Days a habit spent paused do not count against it in history and
 * statistics.
 */
export type HabitPausePeriod = { start: number; end: number | null }

/** The stored/wire form of a pause period, with ISO timestamps. */
export type HabitPausePeriodRecord = { start: string; end: string | null }

/** Older periods are dropped beyond this many, so the JSON column stays small. */
export const MAX_HABIT_PAUSE_PERIODS = 100

/** A pause undone within this long is treated as a mis-tap and leaves no period. */
const ACCIDENTAL_PAUSE_MS = 60 * 1000

/**
 * Parses an ISO string or epoch-ms number into epoch ms.
 *
 * @param value - The candidate value.
 * @returns Epoch ms, or null.
 */
function toMs(value: unknown): number | null {
   if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null
   }
   if (typeof value === 'string' && value) {
      const parsed = Date.parse(value)
      return Number.isFinite(parsed) ? parsed : null
   }
   return null
}

/**
 * Parses one raw period, or null when it has no valid start or ends before it starts.
 *
 * @param entry - The raw period.
 * @returns The period, or null.
 */
function parsePeriod(entry: unknown): HabitPausePeriod | null {
   if (!entry || typeof entry !== 'object') {
      return null
   }
   const record = entry as Record<string, unknown>
   const start = toMs(record.start)
   const end = record.end === null || record.end === undefined ? null : toMs(record.end)
   if (start === null || (end !== null && end < start)) {
      return null
   }
   return { start, end }
}

/**
 * Normalizes pause periods from ISO or epoch-ms input: invalid entries
 * dropped, sorted, overlapping ones merged (an open period absorbs every later
 * one), and capped to the newest {@link MAX_HABIT_PAUSE_PERIODS}.
 *
 * @param value - The candidate list.
 * @returns The normalized periods, oldest first.
 */
export function normalizePausePeriods(value: unknown): HabitPausePeriod[] {
   if (!Array.isArray(value)) {
      return []
   }
   const sorted = value
      .map(parsePeriod)
      .filter((period): period is HabitPausePeriod => period !== null)
      .sort((a, b) => a.start - b.start)
   const merged: HabitPausePeriod[] = []
   for (const period of sorted) {
      const previous = merged[merged.length - 1]
      if (previous && (previous.end === null || period.start <= previous.end)) {
         previous.end =
            previous.end === null || period.end === null ?
               null
            :  Math.max(previous.end, period.end)
         continue
      }
      merged.push({ ...period })
   }
   return merged.slice(-MAX_HABIT_PAUSE_PERIODS)
}

/**
 * Serializes pause periods to their stored/wire form.
 *
 * @param periods - The periods.
 * @returns The periods with ISO timestamps.
 */
export function serializePausePeriods(
   periods: readonly HabitPausePeriod[],
): HabitPausePeriodRecord[] {
   return periods.map(period => ({
      start: new Date(period.start).toISOString(),
      end: period.end === null ? null : new Date(period.end).toISOString(),
   }))
}

/**
 * Records a pause or resume at `nowMs`: pausing opens a period unless one is
 * open, resuming closes the open one (or drops it when it lasted under a
 * minute, so a mis-tap leaves no paused day behind).
 *
 * @param periods - The current periods.
 * @param paused - The new paused state.
 * @param nowMs - When the change happened.
 * @returns The updated periods.
 */
export function applyPausedState(
   periods: readonly HabitPausePeriod[],
   paused: boolean,
   nowMs: number,
): HabitPausePeriod[] {
   const normalized = normalizePausePeriods(periods)
   const open = normalized[normalized.length - 1]
   const isOpen = open !== undefined && open.end === null
   if (paused) {
      return isOpen ? normalized : [...normalized, { start: nowMs, end: null }]
   }
   if (!isOpen) {
      return normalized
   }
   const closed = normalized.slice(0, -1)
   return nowMs - open.start < ACCIDENTAL_PAUSE_MS ?
         closed
      :  [...closed, { start: open.start, end: Math.max(open.start, nowMs) }]
}

/**
 * Returns whether a habit was paused at any moment in `[fromMs, toMs)`. A
 * habit paused before pause periods were recorded (paused, but no open
 * period) counts as paused throughout.
 *
 * @param habit - The paused flag and recorded periods.
 * @param fromMs - Range start.
 * @param toMs - Range end (exclusive).
 * @returns True when the range overlaps a pause.
 */
export function isHabitPausedDuring(
   habit: { paused: boolean; pausePeriods: readonly HabitPausePeriod[] },
   fromMs: number,
   toMs: number,
) {
   const last = habit.pausePeriods[habit.pausePeriods.length - 1]
   if (habit.paused && (last === undefined || last.end !== null)) {
      return true
   }
   return habit.pausePeriods.some(
      period => period.start < toMs && (period.end === null || period.end > fromMs),
   )
}

/**
 * Returns when the habit was last resumed (the end of its latest closed pause
 * period), or null when it is paused or never was.
 *
 * @param habit - The paused flag and recorded periods.
 * @returns Epoch ms of the latest resume, or null.
 */
export function getHabitLastResumedAt(habit: {
   paused: boolean
   pausePeriods: readonly HabitPausePeriod[]
}) {
   const last = habit.pausePeriods[habit.pausePeriods.length - 1]
   return habit.paused || last === undefined ? null : last.end
}
